import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import LazyImg from "./LazyImg";
import { logger } from "../../shared/logger";

/* Swiper (carousel) */
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination as SwiperPagination, A11y } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

/* ---- Safe, namespaced CSS injector (no external file required) ---- */
const NXW_CSS_ID = "nxw-listgrid-css";
const NXW_CSS = `
/* ---------- widget base ---------- */
/* Ensure font inheritance from parent context (WordPress/Elementor) */
#nexus-grid-widget,.nexus-grid-widget,[data-nexus-grid]{font-family:inherit}
.nxw-listgrid-wrap{background:transparent;color:var(--nxw-text,#111827);font-family:inherit;scrollbar-width:none;-ms-overflow-style:none}
.nxw-listgrid-wrap::-webkit-scrollbar{display:none}
.nxw-grid{display:grid;gap:var(--nxw-gap,12px);grid-template-columns:repeat(var(--nxw-cols-base,2),minmax(0,1fr));scrollbar-width:none;-ms-overflow-style:none}
.nxw-grid::-webkit-scrollbar{display:none}
@media (min-width:640px){.nxw-grid{grid-template-columns:repeat(var(--nxw-cols-sm,2),minmax(0,1fr))}}
@media (min-width:768px){.nxw-grid{grid-template-columns:repeat(var(--nxw-cols-md,3),minmax(0,1fr))}}
@media (min-width:1024px){.nxw-grid{grid-template-columns:repeat(var(--nxw-cols-lg,4),minmax(0,1fr))}}
@media (min-width:1500px){.nxw-grid{grid-template-columns:repeat(var(--nxw-cols-xl,4),minmax(0,1fr))}}
.nxw-card{background:var(--nxw-card-bg,#fff);border-radius:var(--nxw-radius,12px);border:1px solid var(--nxw-border,rgba(17,24,39,.12));box-shadow:var(--nxw-shadow,none);overflow:hidden;display:grid;grid-template-rows:auto 1fr;transition:box-shadow .2s ease}
.nxw-media{position:relative;width:100%;background:var(--nxw-img-bg,#f3f4f6)}
.nxw-media::before{content:"";display:block;padding-top:66.6667%}
.nxw-media .nxw-img,
.nxw-media .nxw-media-fill{position:absolute;inset:0;width:100%;height:100%;object-fit:cover}

/* ---------- minimal Swiper base (self-contained) ---------- */
.nxw-swiper{position:absolute;inset:0}
.nxw-swiper .swiper{position:relative;width:100%;height:100%;overflow:hidden}
.nxw-swiper .swiper-wrapper{position:relative;width:100%;height:100%;display:flex;transition-property:transform;box-sizing:content-box}
.nxw-swiper .swiper-slide{flex-shrink:0;width:100%;height:100%;position:relative;display:flex;align-items:stretch}
.nxw-slide-img{width:100%;height:100%;object-fit:cover;pointer-events:none}

/* nav buttons - uses CSS variables for customization via GridWidgetStyleLayer */
.nxw-swiper .swiper-button-prev,
.nxw-swiper .swiper-button-next{
  position:absolute;top:50%;transform:translateY(-50%);z-index:20;
  width:var(--nxw-arrow-size,34px);height:var(--nxw-arrow-size,34px);border-radius:9999px;
  background:var(--nxw-arrow-bg,rgba(0,0,0,.55));box-shadow:none;border:none;
  display:flex;align-items:center;justify-content:center;color:var(--nxw-arrow-color,#ffffff);
  padding:var(--nxw-arrow-padding,8px);
  opacity:0;transition:opacity 0.2s ease;
}
.nxw-card:hover .nxw-swiper .swiper-button-prev,
.nxw-card:hover .nxw-swiper .swiper-button-next{
  opacity:1;
}
.nxw-swiper .swiper-button-prev{ left:6px; }
.nxw-swiper .swiper-button-next{ right:6px; }
/* Hide default Swiper icon content and use custom chevrons */
.nxw-swiper .swiper-button-prev::after,
.nxw-swiper .swiper-button-next::after{
  font-family:inherit;font-size:var(--nxw-arrow-icon-size,18px);color:var(--nxw-arrow-color,#ffffff);line-height:1;font-weight:400;
}
.nxw-swiper .swiper-button-prev::after{
  content:"‹";
}
.nxw-swiper .swiper-button-next::after{
  content:"›";
}
/* Hide any SVG icons Swiper might add */
.nxw-swiper .swiper-button-prev svg,
.nxw-swiper .swiper-button-next svg{
  display:none;
}

/* bullets - uses CSS variables for customization via GridWidgetStyleLayer */
.nxw-swiper .swiper{overflow:visible !important}
.nxw-swiper .swiper-pagination{position:absolute !important;left:0 !important;right:0 !important;bottom:var(--nxw-bullet-bottom,8px) !important;z-index:20 !important;text-align:center !important}
.nxw-swiper .swiper-pagination-bullets{white-space:nowrap !important}
.nxw-swiper .swiper-pagination-bullets .swiper-pagination-bullet{
  display:inline-block !important;box-sizing:border-box !important;
  width:var(--nxw-bullet-size,8px) !important;height:var(--nxw-bullet-size,8px) !important;
  margin:0 var(--nxw-bullet-gap,3px) !important;border-radius:9999px !important;
  opacity:var(--nxw-bullet-opacity,.9) !important;
  background:var(--nxw-bullet-color,#fff) !important;
  border:1px solid var(--nxw-bullet-border-color,rgba(17,24,39,.25)) !important;
}
.nxw-swiper .swiper-pagination-bullets .swiper-pagination-bullet-active{
  background:var(--nxw-bullet-active-color,#111827) !important;
  border-color:var(--nxw-bullet-active-border-color,#111827) !important;
}

/* badge + body + text */
.nxw-badge{position:absolute;top:8px;left:8px;background:var(--nxw-badge,#0ea5e9);color:#fff;font-size:12px;font-weight:700;border-radius:999px;padding:4px 8px}
.nxw-amenity-badge{position:absolute;top:8px;left:8px;z-index:10;background:var(--nxw-amenity-bg,rgba(255,255,255,0.95));color:var(--nxw-amenity-color,#111827);font-size:var(--nxw-amenity-fs,12px);font-weight:var(--nxw-amenity-fw,600);border-radius:var(--nxw-amenity-radius,6px);padding:6px 12px;box-shadow:0 2px 8px rgba(0,0,0,0.15);border:1px solid var(--nxw-amenity-bc,rgba(17,24,39,0.1));pointer-events:none}
.nxw-tags-container{display:flex;flex-wrap:wrap;gap:var(--nxw-tags-gap,6px);margin-top:4px}
.nxw-tag-badge{background:var(--nxw-tag-bg,#ffffff);color:var(--nxw-tag-color,#374151);font-size:var(--nxw-tag-fs,12px);font-weight:var(--nxw-tag-fw,500);border-radius:var(--nxw-tag-radius,6px);padding:4px 10px;border:1px solid var(--nxw-tag-bc,rgba(17,24,39,0.15))}
.nxw-body{padding:var(--nxw-body-padding,16px);display:grid;gap:var(--nxw-body-gap,8px)}
.nxw-loc{font-size:var(--nxw-loc-fs,17px);color:var(--nxw-loc-color,#111827);font-weight:var(--nxw-loc-fw,600);font-style:var(--nxw-loc-style,normal);font-family:var(--nxw-loc-family,inherit)}
.nxw-title{font-size:var(--nxw-title-fs,17px);font-weight:var(--nxw-title-fw,600);line-height:1.3;color:var(--nxw-title-color,#111827);font-style:var(--nxw-title-style,normal);font-family:var(--nxw-title-family,inherit)}
.nxw-meta{font-size:var(--nxw-meta-fs,14px);color:var(--nxw-meta-color,#111827);display:flex;flex-wrap:wrap;gap:0;align-items:center;font-weight:var(--nxw-meta-fw,600);font-style:var(--nxw-meta-style,normal);font-family:var(--nxw-meta-family,inherit)}
.nxw-meta > span:not(:first-child)::before{content:"·";margin:0 4px;color:var(--nxw-meta-color,#6b7280)}
.nxw-meta .nxw-dot::before{content:"·";margin:0 4px;color:var(--nxw-meta-color,#6b7280)}
.nxw-price{margin-top:6px;font-weight:var(--nxw-price-fw,600);font-size:var(--nxw-price-fs,17px);color:var(--nxw-price-color,#111827);white-space:pre-wrap;font-style:var(--nxw-price-style,normal);font-family:var(--nxw-price-family,inherit)}
.nxw-skel{background:#e5e7eb;background-image:linear-gradient(90deg,#e5e7eb,#f3f4f6,#e5e7eb);background-size:400px 100%;animation:nxw-shimmer 1.1s infinite linear;border-radius:8px}
.nxw-skel-line{height:14px;margin-bottom:6px}
.nxw-skel-line.w-40{width:40%}.nxw-skel-line.w-60{width:60%}.nxw-skel-line.w-80{width:80%}
@keyframes nxw-shimmer{0%{background-position:-400px 0}100%{background-position:400px 0}}
.nxw-error{color:var(--nxw-error,#ef4444)}.nxw-empty{color:var(--nxw-muted,#6b7280)}

/* count line and header row above grid */
.nxw-countline{font-size:var(--nxw-count-fs,14px);color:var(--nxw-count-color,#222222);font-weight:var(--nxw-count-fw,600);font-style:var(--nxw-count-style,normal);font-family:var(--nxw-count-family,inherit);text-align:var(--nxw-count-align,left);text-transform:var(--nxw-count-transform,none)}
.nxw-map-filter-note{font-weight:400;color:var(--nxw-count-color,#666666)}
.nxw-header-row{display:flex;flex-direction:column;gap:8px;margin-bottom:12px}
.nxw-header-top{display:flex;justify-content:space-between;align-items:center;gap:8px}
.nxw-header-sort{display:flex;align-items:center}
.nxw-header-sort select{padding:8px 28px 8px 10px;font-size:13px;font-weight:500;color:#374151;background-color:#fff;border:1px solid #d1d5db;border-radius:6px;cursor:pointer;outline:none;appearance:none;background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E");background-position:right 6px center;background-repeat:no-repeat;background-size:16px;width:auto;min-width:0}
.nxw-map-btn{padding:8px 12px;font-size:14px;font-weight:500;color:#374151;background-color:#fff;border:1px solid #d1d5db;border-radius:6px;cursor:pointer;outline:none;display:flex;align-items:center;gap:6px;white-space:nowrap}
@media (min-width:640px){.nxw-header-row{flex-direction:row;justify-content:space-between;align-items:center}.nxw-header-sort{justify-content:flex-end}.nxw-header-sort select{width:auto}}

/* embedded search bar */
.nxw-search-bar-container{width:100%;margin-bottom:16px;display:block!important;visibility:visible!important}
.nxw-search-bar-sticky{position:sticky;top:0;z-index:100;background:var(--nxw-bg,#fff);padding:12px 0;border-bottom:1px solid rgba(0,0,0,0.1)}
@media (max-width:1023px){.nxw-search-bar-container{display:block!important;visibility:visible!important}}
`;

function ensureSwiperCssLink() {
  if (typeof document === "undefined") return;
  if (document.querySelector('link[data-nxw-swiper-css], link[href*="swiper-bundle"], link[href*="swiper.min.css"]')) return;
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = "https://unpkg.com/swiper@10/swiper-bundle.min.css";
  link.setAttribute("data-nxw-swiper-css", "true");
  document.head.appendChild(link);
}

export function ensureFontAwesome(root = document) {
  if (!root || !root.head) return;
  if (root.getElementById("__nx_fa_css")) return;
  const href = "https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.2/css/all.min.css";
  const link = root.createElement("link");
  link.id = "__nx_fa_css";
  link.rel = "stylesheet";
  link.href = href;
  link.crossOrigin = "anonymous";
  link.referrerPolicy = "no-referrer";
  root.head.appendChild(link);
}

function ensureNxwCss() {
  if (typeof document === "undefined") return;
  if (document.getElementById(NXW_CSS_ID)) return;
  const s = document.createElement("style");
  s.id = NXW_CSS_ID;
  s.textContent = NXW_CSS;
  document.head.appendChild(s);
}

const WIDGET_SELECTORS = "#nexus-grid-widget, .nexus-grid-widget, [data-nexus-grid]";

function findWidgetContainer(rootEl) {
  if (!rootEl) return null;
  let el = rootEl;
  while (el && el !== document.documentElement) {
    if (el.matches && el.matches(WIDGET_SELECTORS)) return el;
    el = el.parentElement;
  }
  return document.querySelector(WIDGET_SELECTORS);
}

function readSettingsFromContainer(container) {
  if (!container) return null;
  const d = container.dataset || {};
  const bool = (v, def = true) => {
    if (v == null) return def;
    return String(v).toLowerCase() === "true";
  };
  return {
    showGuests: bool(d.showGuests, true),
    showBedrooms: bool(d.showBedrooms, true),
    showBathrooms: bool(d.showBathrooms, true),
    showLocation: bool(d.showLocation, true),
    iconGuests: d.iconGuests || "fa-users",
    iconBedrooms: d.iconBedrooms || "fa-bed",
    iconBathrooms: d.iconBathrooms || "fa-bath",

    useTextGuests: d.useTextGuests || "",
    useTextBathrooms: d.useTextBathrooms || "",
    useTextBedrooms: d.useTextBedrooms || "",

    imageSize: d.imageSize || "",

    pricePrefix: d.pricePrefix || "",
    priceSuffix: d.priceSuffix || "",
    pricePrefixDated: d.pricePrefixDated || "",
    priceSuffixDated: d.priceSuffixDated || "",
    openLinkNewTab: d.openLinkNewTab || "",
    // NEW: singular/plural affixes for listing count line
    singularPrefix: d.singularPrefix || "",
    singularSuffix: d.singularSuffix || "",
    pluralPrefix: d.pluralPrefix || "",
    pluralSuffix: d.pluralSuffix || "",
    itemOrder: d.itemOrder ? JSON.parse(d.itemOrder) : ["location", "title", "meta", "tags", "price"],
    // Listing count visibility (defaults to true)
    showListingCount: bool(d.showListingCount, true),
  };
}

function sanitize(obj) {
  const out = {};
  for (const [k, v] of Object.entries(obj || {})) {
    if (v === "" || v == null) continue;
    out[k] = v;
  }
  return out;
}

function buildListingHref(it) {
  if (!it || !it.nexus_listing_url_slug) return null;
  const origin =
    typeof window !== "undefined" && window.location && window.location.origin
      ? window.location.origin
      : "";
  if (!origin) return null;
  const slug = String(it.nexus_listing_url_slug).trim();
  if (!slug) return null;
  return `${origin}/listing/${encodeURIComponent(slug)}`;
}

export default function ListGrid({
  items,
  loading,
  error,
  perPage = 6,
  look,
  isCarousel = false,
  textMeta = {},
  onItemHover,
  onItemLeave,
  totalCount,
  selectedCardAmenities = [],
  selectedCardTags = [],
  sortValue = "",
  onSortChange = () => {},
  mapHidden = false,
  onShowMap = () => {},
  lazyLoad = true,
  showTags = true,
  showAmenities = true,
  isFilteredByMap = false,
  virtualScroll = false,
}) {
  const { gap, radius, columns, palette } = look || {};
  const rootRef = useRef(null);
  const gridRef = useRef(null);

  // ---- HARD FALLBACKS that match your API values ----
  // With map (your saved): 3/3/2/2
  const FALLBACK_WITH_MAP = { base: 1, sm: 1, md: 2, lg: 3, xl: 3 };
  // No map (your saved): 4/4/2/1
  const FALLBACK_NO_MAP  = { base: 1, sm: 1, md: 2, lg: 4, xl: 4 };

  // Try to read CSS variables from any injected style layer; if missing,
  // fall back to the map/no-map constants above.
  const [cssCols, setCssCols] = useState(null);
  const [hasMap, setHasMap] = useState(false);

  // Use refs to track previous values and prevent unnecessary re-renders
  const prevCssColsRef = useRef(null);
  const prevHasMapRef = useRef(false);
  const readColsTimeoutRef = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const readFromStyle = (style) => {
      if (!style) return undefined;
      const n = (v) => {
        const x = parseInt(String(v || "").trim(), 10);
        return Number.isFinite(x) ? x : undefined;
      };
      return {
        base: n(style.getPropertyValue("--nxw-cols-base")),
        sm:   n(style.getPropertyValue("--nxw-cols-sm")),
        md:   n(style.getPropertyValue("--nxw-cols-md")),
        lg:   n(style.getPropertyValue("--nxw-cols-lg")),
        xl:   n(style.getPropertyValue("--nxw-cols-xl")),
      };
    };

    // Compare column objects for equality
    const colsEqual = (a, b) => {
      if (a === b) return true;
      if (!a || !b) return false;
      return a.base === b.base && a.sm === b.sm && a.md === b.md && a.lg === b.lg && a.xl === b.xl;
    };

    const readCols = () => {
      const container = findWidgetContainer(rootRef.current || document.body);
      const hm = (container?.dataset?.hasMap || container?.getAttribute("data-has-map")) === "true";

      // Only update hasMap if it actually changed
      if (hm !== prevHasMapRef.current) {
        prevHasMapRef.current = hm;
        setHasMap(hm);
      }

      // Prefer container computed style (that's where override vars land)
      const styleA = container ? getComputedStyle(container) : null;
      const styleB = gridRef.current ? getComputedStyle(gridRef.current) : null;
      const a = readFromStyle(styleA);
      const b = readFromStyle(styleB);

      const pick = (k) => (a?.[k] ?? b?.[k]);
      const next = {
        base: pick("base"),
        sm:   pick("sm"),
        md:   pick("md"),
        lg:   pick("lg"),
        xl:   pick("xl"),
      };

      // If none are present, we'll keep null and use hard fallback below
      // Only update if values actually changed
      if (Object.values(next).some((v) => v !== undefined)) {
        if (!colsEqual(next, prevCssColsRef.current)) {
          prevCssColsRef.current = next;
          setCssCols(next);
        }
      }
    };

    // Debounced version of readCols to prevent rapid-fire updates
    const debouncedReadCols = () => {
      if (readColsTimeoutRef.current) {
        clearTimeout(readColsTimeoutRef.current);
      }
      readColsTimeoutRef.current = setTimeout(readCols, 50);
    };

    // Initial read (not debounced)
    readCols();

    // watch for style tag injections & container attribute flips
    const ids = ["__nxw_dynamic_css_layer", "__nxw_widget_css"];
    const headObs = new MutationObserver(debouncedReadCols);
    headObs.observe(document.head, { childList: true, subtree: true, characterData: true });
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) headObs.observe(el, { childList: true, subtree: true, characterData: true });
    });

    const container = findWidgetContainer(rootRef.current || document.body);
    const attrObs = container ? new MutationObserver(debouncedReadCols) : null;
    if (container && attrObs) attrObs.observe(container, { attributes: true, attributeFilter: ["data-has-map", "style"] });

    return () => {
      headObs.disconnect();
      attrObs?.disconnect();
      if (readColsTimeoutRef.current) {
        clearTimeout(readColsTimeoutRef.current);
      }
    };
  }, []);

  const [ui, setUi] = useState({
    showGuests: true,
    showBedrooms: true,
    showBathrooms: true,
    iconGuests: "fa-users",
    iconBedrooms: "fa-bed",
    iconBathrooms: "fa-bath",

    useTextGuests: "",
    useTextBathrooms: "",
    useTextBedrooms: "",

    pricePrefix: "",
    priceSuffix: "",
    pricePrefixDated: "",
    priceSuffixDated: "",
    hasDates: false,
    guests: 1,
    openLinkNewTab: "",
    nights: 0,
    // NEW: affixes for listing count line
    singularPrefix: "",
    singularSuffix: "",
    pluralPrefix: "",
    pluralSuffix: "",
    // Track listing count visibility
    showListingCount: true,
  });

  useEffect(() => {
    ensureNxwCss();
    ensureSwiperCssLink();
    ensureFontAwesome(document);
  }, []);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const container = findWidgetContainer(root);
    if (!container) return;

    const first = readSettingsFromContainer(container);
    if (first) setUi((u) => ({ ...u, ...first, ...sanitize(textMeta) }));

    const mo = new MutationObserver((mutations) => {
      let changed = false;
      for (const m of mutations) {
        if (m.type === "attributes" && m.attributeName && m.attributeName.startsWith("data-")) {
          changed = true;
        }
      }
      if (changed) {
        const next = readSettingsFromContainer(container);
        if (next) setUi((u) => ({ ...u, ...next, ...sanitize(textMeta) }));
      }
    });
    mo.observe(container, { attributes: true });

    return () => mo.disconnect();
  }, [textMeta]);

  const [queryString, setQueryString] = useState(
    typeof window !== "undefined" ? window.location.search : ""
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const win = window;
    const origPush = win.history.pushState;
    const origReplace = win.history.replaceState;

    const notify = () =>
      setQueryString((prev) =>
        prev !== win.location.search ? win.location.search : prev
      );

    const wrap = (fn) =>
      function (...args) {
        const ret = fn.apply(this, args);
        Promise.resolve().then(notify);
        return ret;
      };

    win.history.pushState = wrap(origPush);
    win.history.replaceState = wrap(origReplace);
    win.addEventListener("popstate", notify);

    return () => {
      win.history.pushState = origPush;
      win.history.replaceState = origReplace;
      win.removeEventListener("popstate", notify);
    };
  }, []);

  const sp = useMemo(() => new URLSearchParams(queryString || ""), [queryString]);

  useEffect(() => {
    const sd = sp.get("startDate");
    const ed = sp.get("endDate");
    const guestsRaw = sp.get("guests");
    const guestsNum = guestsRaw != null ? parseInt(guestsRaw, 10) : NaN;

    const toUtcMidnight = (d) =>
      Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
    const parseISO = (s) => {
      if (!s) return null;
      const d = new Date(s);
      return Number.isNaN(d.getTime()) ? null : d;
    };
    const start = parseISO(sd);
    const end = parseISO(ed);
    const nights = start && end
      ? Math.max(0, Math.round((toUtcMidnight(end) - toUtcMidnight(start)) / 86400000))
      : 0;

    setUi((u) => ({
      ...u,
      hasDates: !!(sd && ed),
      guests: Number.isFinite(guestsNum) ? guestsNum : u.guests, nights,
    }));
  }, [sp]);

  // Effective columns:
  // 1) CSS layer vars if present
  // 2) explicit prop `look.columns`
  // 3) hard fallback based on map presence (matches API you posted)
  const effCols =
    cssCols ||
    columns ||
    (hasMap ? FALLBACK_WITH_MAP : FALLBACK_NO_MAP);

  const vars = useMemo(() => ({
    "--nxw-btn-shadow": palette?.shadow === "none" ? "none" : "0 6px 18px rgba(2,6,23,.12)",
    "--nxw-gap": `${gap ?? 12}px`,
    "--nxw-radius": `${radius ?? 12}px`,
    "--nxw-border": palette?.border || "rgba(17,24,39,.12)",
    "--nxw-shadow": palette?.shadow || "none",
    "--nxw-badge": palette?.badge || "#0ea5e9",
    "--nxw-text": "#111827",
    "--nxw-muted": "#6b7280",
    "--nxw-loc": "#10b981",
    "--nxw-card-bg": "#fff",
    "--nxw-img-bg": "#f3f4f6",
    "--nxw-error": "#ef4444",
    "--nxw-cols-base": String(effCols?.base ?? 1),
    "--nxw-cols-sm": String(effCols?.sm ?? effCols?.base ?? 1),
    "--nxw-cols-md": String(effCols?.md ?? 3),
    "--nxw-cols-lg": String(effCols?.lg ?? 4),
    "--nxw-cols-xl": String(effCols?.xl ?? (effCols?.lg ?? 4)),
  }), [gap, radius, effCols, palette]);

  const skeletonCard = (
    <div className="nxw-card">
      <div className="nxw-media">
        <div className="nxw-skel nxw-media-fill" />
      </div>
      <div className="nxw-body">
        <div className="nxw-skel nxw-skel-line w-40" />
        <div className="nxw-skel nxw-skel-line w-80" />
        <div className="nxw-skel nxw-skel-line w-60" />
      </div>
    </div>
  );

  // ---------- Count line helpers ----------
  const joinWords = (...parts) =>
    parts.map(p => (p ?? "").toString().trim()).filter(Boolean).join(" ");

  const listingCount = (!loading && !error)
    ? (Number.isFinite(Number(totalCount)) ? Number(totalCount) : (Array.isArray(items) ? items.length : 0))
    : 0;
  const isSingular = listingCount === 1;

  const countPrefix = isSingular ? ui.singularPrefix : ui.pluralPrefix;
  const countSuffix = isSingular ? ui.singularSuffix : ui.pluralSuffix;

  const countLine = joinWords(countPrefix, String(listingCount), countSuffix);

  // ---------- Event delegation for card hover (performance optimization) ----------
  // Create a map of items by id for quick lookup
  const itemsById = useMemo(() => {
    if (!items || !Array.isArray(items)) return new Map();
    return new Map(items.map(it => [it.id, it]));
  }, [items]);

  // Event delegation handlers - single handler for all cards instead of one per card
  const handleGridMouseOver = useCallback((e) => {
    if (!onItemHover) return;
    const card = e.target.closest('.nxw-card[data-item-id]');
    if (card) {
      const id = card.dataset.itemId;
      const item = itemsById.get(id) || itemsById.get(Number(id));
      if (item) onItemHover(item.id, item);
    }
  }, [onItemHover, itemsById]);

  const handleGridMouseOut = useCallback((e) => {
    if (!onItemLeave) return;
    const card = e.target.closest('.nxw-card[data-item-id]');
    if (card) {
      // Check if we're leaving to outside the card
      const relatedTarget = e.relatedTarget;
      if (!card.contains(relatedTarget)) {
        const id = card.dataset.itemId;
        const item = itemsById.get(id) || itemsById.get(Number(id));
        if (item) onItemLeave(item.id, item);
      }
    }
  }, [onItemLeave, itemsById]);

  return (
    <div ref={rootRef} className="nxw-listgrid-wrap" style={vars}>
      {/* NEW: count line above the grid with sort dropdown */}
      {!loading && ui.showListingCount && (
        <div className="nxw-header-row">
          <div className="nxw-header-top">
            <div className="nxw-countline" aria-live="polite">
              {countLine}
              {isFilteredByMap && (
                <span className="nxw-map-filter-note"> (within map view)</span>
              )}
            </div>
            {mapHidden && (
              <button
                onClick={onShowMap}
                title="Show map"
                aria-label="Show map"
                className="nxw-map-btn"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"></polygon>
                  <line x1="8" y1="2" x2="8" y2="18"></line>
                  <line x1="16" y1="6" x2="16" y2="22"></line>
                </svg>
                Map
              </button>
            )}
          </div>
          <div className="nxw-header-sort">
            <select
              value={sortValue}
              onChange={(e) => onSortChange(e.target.value)}
            >
              <option value="">Default</option>
              <option value="name">Name (A-Z)</option>
              <option value="bedrooms_desc">Bedrooms -</option>
              <option value="bedrooms_asc">Bedrooms +</option>
              <option value="featured">Featured</option>
              <option value="newest">Newest</option>
            </select>
          </div>
        </div>
      )}

      <div className="nxw-grid" role="list" aria-busy={loading} ref={gridRef} onMouseOver={handleGridMouseOver} onMouseOut={handleGridMouseOut}>
        {loading && Array.from({ length: perPage }).map((_, i) => (
          <React.Fragment key={`sk-${i}`}>{skeletonCard}</React.Fragment>
        ))}

        {!loading && error && <div className="nxw-error" role="alert">Error: {error}</div>}
        {!loading && !error && (!items || items.length === 0) && <div className="nxw-empty">No results.</div>}

        {!loading && !error && items?.map((it, i) => {
          const imgs = Array.isArray(it.images) && it.images.length ? it.images : (it.image ? [it.image] : []);

          // Check for matching amenities and tags
          const listingAmenities = Array.isArray(it.amenities) ? it.amenities : [];
          const listingTags = Array.isArray(it.tags) ? it.tags : [];

          // DEBUG LOGS
          if (i === 0) {
            logger.log('[ListGrid] First listing amenities:', listingAmenities);
            logger.log('[ListGrid] First listing tags:', listingTags);
            logger.log('[ListGrid] Selected card amenities:', selectedCardAmenities);
            logger.log('[ListGrid] Selected card tags:', selectedCardTags);
          }

          // Find matching amenity from selected_card_amenities (show first match only)
          // Match by name (case-insensitive) since API uses different ID systems
          const amenityToShow = selectedCardAmenities.find(cardAmenity => {
            const cardName = (cardAmenity.name || '').toLowerCase().trim();
            return listingAmenities.some(listingAmenity => {
              const listingName = (listingAmenity.name || '').toLowerCase().trim();
              const matches = listingName === cardName;
              if (i === 0 && matches) {
                logger.log('[ListGrid] AMENITY MATCH:', cardName, '===', listingName);
              }
              return matches;
            });
          });

          // Find all matching tags from selected_card_tags
          // Match by name (case-insensitive)
          const matchingTags = selectedCardTags.filter(cardTag => {
            const cardName = (cardTag.name || '').toLowerCase().trim();
            return listingTags.some(listingTag => {
              const listingName = typeof listingTag === 'string'
                ? listingTag.toLowerCase().trim()
                : (listingTag.name || '').toLowerCase().trim();
              const matches = listingName === cardName;
              if (i === 0 && matches) {
                logger.log('[ListGrid] TAG MATCH:', cardName, '===', listingName);
              }
              return matches;
            });
          });

          if (i === 0) {
            logger.log('[ListGrid] Amenity to show:', amenityToShow);
            logger.log('[ListGrid] Matching tags:', matchingTags);
          }

          const replaceNightsSmart = (str) => {
            if (!str) return "";
            if (!ui.hasDates || !ui.nights) return str;
            if (!/\{x\}/i.test(str)) return str;
            let out = String(str).replace(/\{x\}/gi, String(ui.nights));
            out = out.replace(/\b(nights?)\b/gi, (m) => (ui.nights === 1 ? m.toLowerCase().replace(/s$/, "") : m.toLowerCase().endsWith("s") ? m.toLowerCase() : m.toLowerCase()+"s"));
            return out;
          };

          const prefix = ui.hasDates ? replaceNightsSmart(ui.pricePrefixDated) : ui.pricePrefix;
          const suffix = ui.hasDates ? replaceNightsSmart(ui.priceSuffixDated) : ui.priceSuffix;

          const cityState = [it.city, it.state].filter(Boolean).join(", ");
          const href = buildListingHref(it);

          const content = (
            <>
              <div className="nxw-media">
                {isCarousel && imgs.length > 1 ? (
                  <div className="nxw-swiper">
                    <Swiper
                      modules={[Navigation, SwiperPagination, A11y]}
                      slidesPerView={1}
                      spaceBetween={0}
                      loop
                      navigation
                      pagination={{ clickable: true }}
                      observeParents
                      observer
                      resizeObserver
                      onInit={(s) => requestAnimationFrame(() => s.update())}
                    >
                      {imgs.slice(0, 5).map((src, k) => (
                        <SwiperSlide key={k}>
                          <LazyImg src={src} alt={`${it.title} – image ${k + 1}`} className="nxw-slide-img" lazy={lazyLoad} />
                        </SwiperSlide>
                      ))}
                    </Swiper>
                  </div>
                ) : (
                  <>
                    {imgs[0] ? (
                      <LazyImg src={imgs[0]} alt={it.title} className="nxw-img" lazy={lazyLoad} />
                    ) : (
                      <div className="nxw-media-fill" aria-hidden="true" />
                    )}
                  </>
                )}
                {it.isFeatured && <span className="nxw-badge">Featured</span>}
                {showAmenities && amenityToShow && <span className="nxw-amenity-badge">{amenityToShow.name}</span>}
              </div>

              <div className="nxw-body">
                {renderCardItems()}
              </div>
            </>
          );

          // Helper to render card items in specified order
          function renderCardItems() {
            const itemOrder = ui.itemOrder || ["location", "title", "meta", "tags", "price"];
            const itemMap = {
              location: ui.showLocation && cityState && <div key="location" className="nxw-loc">{cityState}</div>,
              title: <div key="title" className="nxw-title">{it.title}</div>,
              meta: <div key="meta" className="nxw-meta">
                  {ui.showGuests && Number.isFinite(it.guests) && (
                    <span
                      className="nxw-guest"
                      aria-label={`${it.guests} guest${it.guests === 1 ? '' : 's'}`}
                      title={`${it.guests} guest${it.guests === 1 ? '' : 's'}`}
                    >
                      {ui.useTextGuests ? (
                        <span className="nxw-meta-text">
                          {it.guests} {it.guests === 1 ? 'Guest' : 'Guests'}
                        </span>
                      ) : (
                        <>
                          <i
                            className={`fa-solid ${ui.iconGuests}`}
                            aria-hidden="true"
                            style={{ marginRight: 6 }}
                          />
                          <span className="nxw-meta-num">{it.guests}</span>
                        </>
                      )}
                    </span>
                  )}

                  {ui.showBedrooms && Number.isFinite(it.bedrooms) && (
                    <span
                      className="nxw-bed"
                      aria-label={`${it.bedrooms} bedroom${it.bedrooms === 1 ? '' : 's'}`}
                      title={`${it.bedrooms} bedroom${it.bedrooms === 1 ? '' : 's'}`}
                    >
                      {ui.useTextBedrooms ? (
                        <span className="nxw-meta-text">
                          {it.bedrooms} {it.bedrooms === 1 ? 'Bedroom' : 'Bedrooms'}
                        </span>
                      ) : (
                        <>
                          <i
                            className={`fa-solid ${ui.iconBedrooms}`}
                            aria-hidden="true"
                            style={{ marginRight: 6 }}
                          />
                          <span className="nxw-meta-num">{it.bedrooms}</span>
                        </>
                      )}
                    </span>
                  )}

                  {ui.showBathrooms && Number.isFinite(it.bathrooms) && (
                    <span
                      className="nxw-bath"
                      aria-label={`${it.bathrooms} bathroom${it.bathrooms === 1 ? '' : 's'}`}
                      title={`${it.bathrooms} bathroom${it.bathrooms === 1 ? '' : 's'}`}
                    >
                      {ui.useTextBathrooms ? (
                        <span className="nxw-meta-text">
                          {it.bathrooms} {it.bathrooms === 1 ? 'Bathroom' : 'Bathrooms'}
                        </span>
                      ) : (
                        <>
                          <i
                            className={`fa-solid ${ui.iconBathrooms}`}
                            aria-hidden="true"
                            style={{ marginRight: 6 }}
                          />
                          <span className="nxw-meta-num">{it.bathrooms}</span>
                        </>
                      )}
                    </span>
                  )}

                </div>,
              tags: showTags && matchingTags.length > 0 && (
                <div key="tags" className="nxw-tags-container">
                  {matchingTags.map((tag, idx) => (
                    <span key={`tag-${idx}`} className="nxw-tag-badge">
                      {tag.name}
                    </span>
                  ))}
                </div>
              ),
              price: it.price && (
                <div key="price" className="nxw-price">
                  {prefix && <span className="nxw-price-prefix">{prefix}</span>}
                  <span className="nxw-price-amount">{it.price}</span>
                  {suffix && <span className="nxw-price-suffix"> {suffix}</span>}
                </div>
              ),
            };

            return itemOrder.map(key => itemMap[key]).filter(Boolean);
          }

          return (
            <div
              key={`${it.id}-${i}`}
              id={`nexus-card-${it.id}`}
              data-item-id={it.id}
              className="nxw-card"
              role="listitem"
            >
              {href ? (
                <a
                  href={href}
                  target={ui.openLinkNewTab ? "_blank" : "_self"} rel="noopener noreferrer"
                  className="nxw-link"
                  style={{ color: "inherit", textDecoration: "none", display: "block" }}
                >
                  {content}
                </a>
              ) : (
                content
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
